import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Storefront, Code, Lightning, ShoppingCart, Tag, Gear, ArrowRight, Info, Package, CreditCard, Eye, Mouse } from "@phosphor-icons/react";

const ShopifyIntegrationTab = ({ apiUrl }) => {
  const [copied, setCopied] = useState({});

  const widgetCdnUrl = `${apiUrl}/api/widget.js`;

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(prev => ({ ...prev, [key]: true }));
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
    } catch { toast.error("Failed to copy"); }
  };

  const CopyBtn = ({ id, label = "Copy" }) => (
    <button
      onClick={() => copyToClipboard(snippets[id], id)}
      data-testid={`copy-shopify-${id}`}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
        copied[id]
          ? "bg-[#96BF48]/20 border border-[#96BF48] text-[#96BF48]"
          : "bg-[#96BF48] text-black hover:opacity-85"
      }`}
    >
      {copied[id] ? <><Check size={13} weight="bold" /> Copied!</> : <><Copy size={13} weight="bold" /> {label}</>}
    </button>
  );

  const StepNumber = ({ n }) => (
    <span className="w-7 h-7 rounded-full bg-[#96BF48]/20 text-[#96BF48] text-xs font-bold flex items-center justify-center flex-shrink-0">{n}</span>
  );

  const snippets = {
    themeLiquid: `<!-- Portl Sales Agent — Add before </body> in theme.liquid -->
<script src="${widgetCdnUrl}" defer></script>`,

    appEmbed: `// For Shopify 2.0 Themes (App Embed Block):
// 1. Go to Online Store > Themes > Customize
// 2. Click "App embeds" in the left sidebar
// 3. Toggle ON "Portl Sales Agent"
//
// If using a custom app, add this ScriptTag via Admin API:
// POST /admin/api/2024-01/script_tags.json
{
  "script_tag": {
    "event": "onload",
    "src": "${widgetCdnUrl}"
  }
}`,

    shopifyTracking: `<!-- Add to theme.liquid (before </body>, after widget.js) -->
<script>
(function() {
  // Wait for widget to initialize
  function onWidgetReady(cb) {
    if (window.portlTrack) return cb();
    var t = setInterval(function() {
      if (window.portlTrack) { clearInterval(t); cb(); }
    }, 200);
    setTimeout(function() { clearInterval(t); }, 10000);
  }

  onWidgetReady(function() {
    var meta = window.ShopifyAnalytics && ShopifyAnalytics.meta || {};
    var page = meta.page || {};

    // ---- Product Page Detection ----
    if (page.pageType === 'product') {
      var title = (meta.product && meta.product.type || '').toLowerCase();
      var handle = window.location.pathname.split('/').pop().toLowerCase();

      if (handle.includes('ultragym') || title.includes('ultragym')) {
        portlTrack('product_page_ultragym');
      } else if (handle.includes('studio') || title.includes('studio')) {
        portlTrack('product_page_studio');
      }
      portlTrack('shopify_product_view');

      // Track price viewing (scroll to price element)
      var priceEl = document.querySelector('.product__price, .price, [data-product-price]');
      if (priceEl) {
        new IntersectionObserver(function(entries) {
          entries.forEach(function(e) {
            if (e.isIntersecting) portlTrack('pricing_scroll');
          });
        }, { threshold: 0.5 }).observe(priceEl);
      }
    }

    // ---- Collection / Category Page ----
    if (page.pageType === 'collection') {
      portlTrack('shopify_collection_view');
    }

    // ---- Cart Page ----
    if (window.location.pathname === '/cart') {
      portlTrack('shopify_cart_view');
    }

    // ---- Add to Cart Tracking ----
    // Method 1: Form submit interception
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function(form) {
      form.addEventListener('submit', function() {
        portlTrack('buy_now_click');
      });
    });

    // Method 2: AJAX Add to Cart (Shopify themes using fetch/XHR)
    var origFetch = window.fetch;
    window.fetch = function() {
      var url = arguments[0];
      if (typeof url === 'string' && url.includes('/cart/add')) {
        portlTrack('buy_now_click');
      }
      return origFetch.apply(this, arguments);
    };

    // Method 3: Button click fallback
    document.querySelectorAll(
      'button[name="add"], [data-add-to-cart], .product-form__submit, .btn--add-to-cart, .add-to-cart'
    ).forEach(function(btn) {
      btn.addEventListener('mouseenter', function() { portlTrack('buy_now_hover'); });
      btn.addEventListener('click', function() { portlTrack('buy_now_click'); });
    });

    // ---- Checkout Intent ----
    document.querySelectorAll(
      'a[href*="/checkout"], button[name="checkout"], .cart__checkout-button, .cart__checkout'
    ).forEach(function(el) {
      el.addEventListener('click', function() {
        portlTrack('checkout_intent');
      });
    });

    // ---- Buy Now / Quick Buy Buttons ----
    document.querySelectorAll(
      '.shopify-payment-button button, .shopify-payment-button__button, [data-shopify="payment-button"]'
    ).forEach(function(btn) {
      btn.addEventListener('click', function() {
        portlTrack('buy_now_click');
      });
    });
  });
})();
</script>`,

    cartAjaxTracking: `<!-- Optional: Real-time cart change tracking -->
<!-- Add after the Shopify tracking script above -->
<script>
(function() {
  function onWidgetReady(cb) {
    if (window.portlTrack) return cb();
    var t = setInterval(function() {
      if (window.portlTrack) { clearInterval(t); cb(); }
    }, 200);
    setTimeout(function() { clearInterval(t); }, 10000);
  }

  onWidgetReady(function() {
    // Listen for Shopify's cart change events
    document.addEventListener('cart:updated', function() {
      portlTrack('cart_updated');
    });

    // Intercept XMLHttpRequest for AJAX cart updates
    var origXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url) {
      if (url && (url.includes('/cart/change') || url.includes('/cart/update'))) {
        this.addEventListener('load', function() {
          portlTrack('cart_updated');
        });
      }
      return origXHROpen.apply(this, arguments);
    };

    // Track quantity changes on cart page
    document.querySelectorAll(
      '.cart__quantity-selector input, [data-quantity-input], .quantity-selector'
    ).forEach(function(input) {
      input.addEventListener('change', function() {
        portlTrack('cart_updated');
      });
    });

    // Track remove from cart
    document.querySelectorAll(
      '.cart__remove, [data-cart-remove], a[href*="/cart/change?quantity=0"]'
    ).forEach(function(btn) {
      btn.addEventListener('click', function() {
        portlTrack('cart_item_removed');
      });
    });
  });
})();
</script>`,

    liquidProductMeta: `{% comment %} Optional: Expose product data to Arya {% endcomment %}
{% comment %} Add inside your product template (product.liquid / main-product.liquid) {% endcomment %}

{% if template contains 'product' %}
<script>
  window.__portlProductContext = {
    id: {{ product.id }},
    title: {{ product.title | json }},
    handle: {{ product.handle | json }},
    type: {{ product.type | json }},
    price: {{ product.price | money_without_currency | json }},
    compareAtPrice: {{ product.compare_at_price | money_without_currency | json }},
    available: {{ product.available }},
    vendor: {{ product.vendor | json }},
    tags: {{ product.tags | json }}
  };
</script>
{% endif %}`,

    checkoutExtension: `// For Shopify Plus: Checkout Extensibility
// Add to checkout.liquid or use Checkout UI Extensions

// Track checkout steps
Shopify.Checkout.OrderStatus.addContentBox(function() {
  if (window.portlTrack) {
    portlTrack('purchase_complete');
  }
});

// Or use Shopify Analytics events
analytics.subscribe('checkout_completed', function(event) {
  // Fire conversion event to Portl
  fetch('${apiUrl}/api/score', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      session_id: localStorage.getItem('portl_session_id'),
      event_type: 'purchase_complete',
      points: 15
    })
  });
});`
  };

  const shopifyEvents = [
    { name: "shopify_product_view", points: 2, desc: "Viewed any product page", icon: Eye, trigger: "ShopifyAnalytics.meta" },
    { name: "product_page_ultragym", points: 2, desc: "Viewed UltraGym product", icon: Eye, trigger: "URL + product handle" },
    { name: "product_page_studio", points: 2, desc: "Viewed Studio product", icon: Eye, trigger: "URL + product handle" },
    { name: "shopify_collection_view", points: 1, desc: "Browsed a collection", icon: Package, trigger: "pageType detection" },
    { name: "shopify_cart_view", points: 4, desc: "Visited cart page", icon: ShoppingCart, trigger: "URL /cart" },
    { name: "pricing_scroll", points: 8, desc: "Price element in viewport", icon: Mouse, trigger: "IntersectionObserver" },
    { name: "buy_now_hover", points: 8, desc: "Hovered Add to Cart", icon: Mouse, trigger: "mouseenter" },
    { name: "buy_now_click", points: 12, desc: "Clicked Add to Cart", icon: ShoppingCart, trigger: "form submit + fetch intercept" },
    { name: "checkout_intent", points: 10, desc: "Clicked Checkout button", icon: CreditCard, trigger: "click listener" },
    { name: "cart_updated", points: 3, desc: "Changed cart contents", icon: ShoppingCart, trigger: "XHR + event intercept" },
    { name: "cart_item_removed", points: -2, desc: "Removed cart item", icon: ShoppingCart, trigger: "click listener" },
    { name: "purchase_complete", points: 15, desc: "Completed purchase", icon: CreditCard, trigger: "Checkout Extensions" },
  ];

  return (
    <div className="space-y-6" data-testid="shopify-integration-tab">

      {/* Header Card */}
      <div className="bg-[#121212] border border-[#96BF48]/40 rounded-sm p-6" data-testid="shopify-header">
        <div className="flex items-center gap-3 mb-2">
          <Storefront size={28} weight="bold" className="text-[#96BF48]" />
          <div>
            <h2 className="text-lg font-semibold text-white">Shopify Integration</h2>
            <p className="text-sm text-[#A0A0A0]">Deploy Arya + product tracking on your Shopify store</p>
          </div>
        </div>
        <div className="mt-4 p-3 bg-[#96BF48]/8 border border-[#96BF48]/20 rounded">
          <p className="text-xs text-[#C0C0C0] leading-relaxed">
            <span className="text-[#96BF48] font-semibold">What you get:</span> AI chat widget (Arya), automatic product page detection, Add to Cart / Checkout intent tracking, cart change monitoring, and real-time lead scoring — all feeding into this dashboard.
          </p>
        </div>
      </div>

      {/* ===== Step 1: Install Widget Script ===== */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6" data-testid="shopify-step1">
        <div className="flex items-center gap-3 mb-4">
          <StepNumber n="1" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">Add the Widget Script</h3>
            <p className="text-xs text-[#707070]">Choose your preferred installation method</p>
          </div>
        </div>

        {/* Method A: theme.liquid */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-[#96BF48] bg-[#96BF48]/15 px-2 py-0.5 rounded">RECOMMENDED</span>
              <h4 className="text-xs font-medium text-white">Method A: Edit theme.liquid</h4>
            </div>
            <CopyBtn id="themeLiquid" />
          </div>
          <pre className="bg-[#050505] border border-[#2A2A2A] rounded p-3 overflow-x-auto text-xs font-mono text-[#A0A0A0] mb-2">
            <code>{snippets.themeLiquid}</code>
          </pre>
          <div className="flex flex-col gap-1.5 ml-1">
            {[
              "Go to Online Store > Themes > Actions > Edit code",
              "Open Layout > theme.liquid",
              "Paste the script just before the closing </body> tag",
              "Click Save"
            ].map((step) => (
              <div key={step} className="flex items-start gap-2 text-xs text-[#888]">
                <ArrowRight size={11} className="text-[#96BF48] mt-0.5 flex-shrink-0" />
                <span>{step}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Method B: App Embed / ScriptTag API */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-[#555] bg-[#333] px-2 py-0.5 rounded">ALTERNATIVE</span>
              <h4 className="text-xs font-medium text-white">Method B: App Embed / ScriptTag API</h4>
            </div>
            <CopyBtn id="appEmbed" />
          </div>
          <pre className="bg-[#050505] border border-[#2A2A2A] rounded p-3 overflow-x-auto text-xs font-mono text-[#A0A0A0]">
            <code>{snippets.appEmbed}</code>
          </pre>
        </div>
      </div>

      {/* ===== Step 2: Shopify-Specific Tracking ===== */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6" data-testid="shopify-step2">
        <div className="flex items-center gap-3 mb-4">
          <StepNumber n="2" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">Add Shopify Product & Cart Tracking</h3>
            <p className="text-xs text-[#707070]">Auto-detect product views, Add to Cart, and checkout intent</p>
          </div>
          <CopyBtn id="shopifyTracking" />
        </div>
        <pre className="bg-[#050505] border border-[#2A2A2A] rounded p-3 overflow-x-auto text-xs font-mono text-[#A0A0A0] max-h-[400px]">
          <code>{snippets.shopifyTracking}</code>
        </pre>
        <div className="mt-3 p-3 bg-[#0A0A0A] border border-[#2A2A2A] rounded">
          <p className="text-[11px] text-[#888] leading-relaxed">
            <Info size={12} className="text-[#96BF48] inline mr-1" />
            This script uses <code className="text-[#96BF48]">ShopifyAnalytics.meta</code> for page type detection and intercepts
            both form submissions and <code className="text-[#96BF48]">fetch()</code> calls to <code className="text-[#96BF48]">/cart/add</code> for
            accurate Add to Cart tracking across all Shopify themes (Dawn, Debut, custom, etc.).
          </p>
        </div>
      </div>

      {/* ===== Step 3: Cart AJAX Tracking (Optional) ===== */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6" data-testid="shopify-step3">
        <div className="flex items-center gap-3 mb-4">
          <StepNumber n="3" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">Real-time Cart Change Tracking</h3>
            <p className="text-xs text-[#707070]">Optional — monitors quantity changes and item removals</p>
          </div>
          <CopyBtn id="cartAjaxTracking" />
        </div>
        <pre className="bg-[#050505] border border-[#2A2A2A] rounded p-3 overflow-x-auto text-xs font-mono text-[#A0A0A0] max-h-[300px]">
          <code>{snippets.cartAjaxTracking}</code>
        </pre>
      </div>

      {/* ===== Step 4: Product Data Exposure (Optional) ===== */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6" data-testid="shopify-step4">
        <div className="flex items-center gap-3 mb-4">
          <StepNumber n="4" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">Expose Product Data to Arya</h3>
            <p className="text-xs text-[#707070]">Optional — gives Arya access to current product context via Liquid</p>
          </div>
          <CopyBtn id="liquidProductMeta" />
        </div>
        <pre className="bg-[#050505] border border-[#2A2A2A] rounded p-3 overflow-x-auto text-xs font-mono text-[#A0A0A0]">
          <code>{snippets.liquidProductMeta}</code>
        </pre>
        <div className="flex flex-col gap-1.5 ml-1 mt-3">
          {[
            "Go to Online Store > Themes > Edit code",
            "Open Sections > main-product.liquid (or product-template.liquid)",
            "Paste inside the product template section",
            "Arya can now reference product title, price, and availability in conversations"
          ].map((step) => (
            <div key={step} className="flex items-start gap-2 text-xs text-[#888]">
              <ArrowRight size={11} className="text-[#96BF48] mt-0.5 flex-shrink-0" />
              <span>{step}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ===== Step 5: Checkout Tracking (Shopify Plus) ===== */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6" data-testid="shopify-step5">
        <div className="flex items-center gap-3 mb-4">
          <StepNumber n="5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">Checkout & Purchase Tracking</h3>
            <p className="text-xs text-[#707070]">Shopify Plus only — track completed purchases</p>
          </div>
          <CopyBtn id="checkoutExtension" />
        </div>
        <pre className="bg-[#050505] border border-[#2A2A2A] rounded p-3 overflow-x-auto text-xs font-mono text-[#A0A0A0]">
          <code>{snippets.checkoutExtension}</code>
        </pre>
        <div className="mt-3 p-2 bg-[#FF9F0A]/8 border border-[#FF9F0A]/30 rounded">
          <p className="text-[11px] text-[#FF9F0A]">
            <Info size={12} className="inline mr-1" />
            Checkout script injection requires Shopify Plus. Standard Shopify plans can use the post-purchase "Thank You" page script or Shopify Web Pixels.
          </p>
        </div>
      </div>

      {/* ===== Shopify Event Reference Table ===== */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6" data-testid="shopify-events">
        <h2 className="text-lg font-semibold text-white mb-1">Shopify Event Reference</h2>
        <p className="text-xs text-[#555] mb-4">Events tracked on your Shopify store. Each fires once per session. Score capped at 100.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left border-b border-[#2A2A2A]">
                <th className="py-2 pr-4 text-[#707070] font-medium">Event</th>
                <th className="py-2 pr-4 text-[#707070] font-medium">Points</th>
                <th className="py-2 pr-4 text-[#707070] font-medium">Description</th>
                <th className="py-2 text-[#707070] font-medium">Detection</th>
              </tr>
            </thead>
            <tbody>
              {shopifyEvents.map(ev => (
                <tr key={ev.name} className="border-b border-[#1A1A1A] hover:bg-[#0A0A0A]">
                  <td className="py-2 pr-4">
                    <code className="text-[#96BF48] font-mono">{ev.name}</code>
                  </td>
                  <td className="py-2 pr-4">
                    <span className={`font-bold ${ev.points >= 0 ? 'text-[#96BF48]' : 'text-[#FF3B30]'}`}>
                      {ev.points >= 0 ? '+' : ''}{ev.points}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-[#A0A0A0]">{ev.desc}</td>
                  <td className="py-2 text-[#555] font-mono text-[10px]">{ev.trigger}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== Compatibility Note ===== */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6" data-testid="shopify-compat">
        <div className="flex items-center gap-3 mb-4">
          <Gear size={22} weight="bold" className="text-[#888]" />
          <h2 className="text-sm font-semibold text-white">Theme Compatibility</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { theme: "Dawn (default)", status: "Full support", color: "#32D74B" },
            { theme: "Debut / Brooklyn", status: "Full support", color: "#32D74B" },
            { theme: "Prestige / Impulse", status: "Full support", color: "#32D74B" },
            { theme: "Custom / Headless", status: "Widget works, tracking needs selector tuning", color: "#FFD60A" },
          ].map(t => (
            <div key={t.theme} className="flex items-center justify-between px-3 py-2.5 bg-[#0A0A0A] border border-[#2A2A2A] rounded text-xs">
              <span className="text-white font-medium">{t.theme}</span>
              <span className="font-medium" style={{ color: t.color }}>{t.status}</span>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[#555] mt-3">
          The chat widget works on all Shopify themes. Product and cart tracking uses standard Shopify DOM selectors
          that cover 95%+ of themes. For heavily customized themes, use the <code className="text-[#96BF48]">portlTrack()</code> API for manual event firing.
        </p>
      </div>
    </div>
  );
};

export default ShopifyIntegrationTab;
